import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getTasks, approveTask, getTask, downloadAttachment } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ShieldCheck, XCircle, Eye, Users, Calendar, Flag, ChevronRight, X, Paperclip, FileText, ExternalLink, Download, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';

// Priority scale is dept-aware: D&D uses 1-5, others use 1-10
const getPriorityInfo = (p, dept) => {
  const isDD = dept === 'Design & Development';
  const label = isDD
    ? (p === 1 ? 'High' : p <= 3 ? 'Medium' : 'Low')
    : (p <= 3 ? 'High' : p <= 6 ? 'Medium' : 'Low');
  const color = label === 'High' ? 'bg-red-500/15 text-red-600 border-red-500/20'
    : label === 'Medium' ? 'bg-yellow-500/15 text-yellow-600 border-yellow-500/20'
    : 'bg-green-500/15 text-green-600 border-green-500/20';
  return { label, color };
};

const ApprovalsPage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState([]);

  const isSuperAdmin = user?.role === 'Super Admin';
  const myDepts = (user?.departments && user.departments.length) ? user.departments : (user?.department ? [user.department] : []);
  const isApprovalUser = isSuperAdmin || myDepts.some(d => ['Design & Development', 'Marketing', 'Sales Coordinators'].includes(d));
  // Can this user act on a given task's approval? (Super Admin only if explicitly named an approver)
  const canApproveTask = (t) => {
    const ap = t.approvers || [];
    if (ap.length) return ap.includes(user?.id);
    return user?.role === 'Admin' && myDepts.includes(t.department);
  };

  const fetchTasks = useCallback(async () => {
    try {
      const res = await getTasks();
      const reviewTasks = res.data.filter(t => t.status === 'In Review' && canApproveTask(t));
      setTasks(reviewTasks);
    } catch { toast.error('Failed to fetch tasks'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Assignee-name multi-select filter
  const assigneeNames = [...new Set(tasks.map(t => t.assigned_to_name).filter(Boolean))].sort();
  const visibleTasks = assigneeFilter.length ? tasks.filter(t => assigneeFilter.includes(t.assigned_to_name)) : tasks;
  const toggleAssignee = (name) => setAssigneeFilter(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    const visIds = visibleTasks.map(t => t.id);
    const allSelected = visIds.length > 0 && visIds.every(id => selected.includes(id));
    setSelected(allSelected ? selected.filter(id => !visIds.includes(id)) : [...new Set([...selected, ...visIds])]);
  };

  const handleBulkAction = async (action) => {
    if (selected.length === 0) { toast.error('Select at least one task'); return; }
    if (action === 'reject' && !rejectReason.trim()) {
      toast.error('Remark is mandatory for rejection');
      return;
    }
    setProcessing(true);
    let success = 0, fail = 0;
    for (const taskId of selected) {
      try {
        await approveTask(taskId, { action, reason: rejectReason });
        success++;
      } catch { fail++; }
    }
    toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'} ${success} task(s)${fail ? `, ${fail} failed` : ''}`);
    setSelected([]);
    setRejectReason('');
    fetchTasks();
    setProcessing(false);
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await getTask(id);
      setDetailTask(res.data);
    } catch { toast.error('Failed to load task'); }
    finally { setDetailLoading(false); }
  };

  const handleDownloadAttachment = async (taskId, att) => {
    try {
      const res = await downloadAttachment(taskId, att.id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download attachment');
    }
  };

  const [previewAtt, setPreviewAtt] = useState(null);  // { url, type, name }
  const handleViewAttachment = async (taskId, att) => {
    try {
      const ext = (att.filename || '').split('.').pop().toLowerCase();
      const isPdf = ext === 'pdf';
      const isImage = ['png','jpg','jpeg','gif','webp'].includes(ext);
      if (!isPdf && !isImage) {
        toast.info('Preview not supported for this file. Downloading instead.');
        await handleDownloadAttachment(taskId, att);
        return;
      }
      const res = await downloadAttachment(taskId, att.id);
      const blob = new Blob([res.data], { type: isPdf ? 'application/pdf' : `image/${ext === 'jpg' ? 'jpeg' : ext}` });
      const url = window.URL.createObjectURL(blob);
      setPreviewAtt({ url, type: isPdf ? 'pdf' : 'image', name: att.filename });
    } catch {
      toast.error('Failed to load preview');
    }
  };
  
  const closePreview = () => {
    if (previewAtt?.url) window.URL.revokeObjectURL(previewAtt.url);
    setPreviewAtt(null);
  };

  if (!isApprovalUser) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Approvals are only available to departments with an approval workflow.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto" data-testid="approvals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Approvals</h1>
          <p className="text-sm text-muted-foreground">{visibleTasks.length} task(s) pending review{assigneeFilter.length ? ` (filtered)` : ''}</p>
        </div>
      </div>

      {/* Filter by assignee name (multi-select) */}
      {assigneeNames.length > 0 && (
        <Card className="border-border/50" data-testid="approvals-filter-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground mr-1"><Filter className="h-4 w-4" /> Filter by assignee:</span>
              {assigneeNames.map(name => {
                const active = assigneeFilter.includes(name);
                return (
                  <button
                    key={name}
                    onClick={() => toggleAssignee(name)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-[#F5A623] text-black border-[#F5A623] font-semibold' : 'border-border/60 hover:border-[#F5A623]/50 text-muted-foreground'}`}
                    data-testid={`assignee-filter-${name}`}
                  >
                    {name}
                  </button>
                );
              })}
              {assigneeFilter.length > 0 && (
                <button onClick={() => setAssigneeFilter([])} className="text-xs px-2 py-1 text-[#F5A623] hover:underline flex items-center gap-1" data-testid="clear-assignee-filter">
                  <X className="h-3 w-3" /> Clear
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Actions */}
      {visibleTasks.length > 0 && (
        <Card className="border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={visibleTasks.length > 0 && visibleTasks.every(t => selected.includes(t.id))} onCheckedChange={toggleAll} data-testid="select-all-approvals" />
                Select All ({visibleTasks.filter(t => selected.includes(t.id)).length}/{visibleTasks.length})
              </label>
              <Separator orientation="vertical" className="h-6" />
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleBulkAction('approve')}
                disabled={processing || selected.length === 0} data-testid="bulk-approve-btn">
                <ShieldCheck className="h-4 w-4 mr-1" /> Approve ({selected.length})
              </Button>
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Textarea placeholder="Rejection remark (mandatory)" value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  rows={1} className="text-sm h-9 min-h-[36px] resize-none" data-testid="bulk-reject-reason" />
                <Button size="sm" variant="destructive" onClick={() => handleBulkAction('reject')}
                  disabled={processing || selected.length === 0} data-testid="bulk-reject-btn">
                  <XCircle className="h-4 w-4 mr-1" /> Reject
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Task List */}
        <div className="lg:col-span-2 space-y-2">
          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]" /></div>
          ) : visibleTasks.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-muted-foreground">{assigneeFilter.length ? 'No tasks match the selected assignee(s)' : 'No tasks pending approval'}</CardContent></Card>
          ) : visibleTasks.map(task => {
            const pri = getPriorityInfo(task.priority, task.department);
            const isSelected = selected.includes(task.id);
            return (
              <Card key={task.id} className={`border-border/50 cursor-pointer transition-all hover:border-[#F5A623]/40 ${isSelected ? 'ring-2 ring-[#F5A623]/50 bg-[#F5A623]/5' : ''} ${detailTask?.id === task.id ? 'border-[#F5A623]' : ''}`}
                data-testid={`approval-task-${task.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(task.id)} className="mt-1" />
                    <div className="flex-1 min-w-0" onClick={() => openDetail(task.id)}>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm truncate">{task.task_name}</p>
                        {task.work_type === 'Team' && (
                          <Badge className="text-[9px] px-1 py-0 bg-blue-500/15 text-blue-600 border-blue-500/20"><Users className="h-2.5 w-2.5 mr-0.5 inline" />Team</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Flag className="h-3 w-3" /><Badge className={`text-[10px] px-1 py-0 border ${pri.color}`}>{pri.label}</Badge></span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(task.due_date).toLocaleDateString()}</span>
                        <span>{task.assigned_to_name}</span>
                        {task.work_type === 'Team' && task.assigned_to_names && (
                          <span>+ {task.assigned_to_names.length - 1} more</span>
                        )}
                        {task.attachments && task.attachments.length > 0 && (
                          <span className="flex items-center gap-1 text-[#F5A623]" data-testid={`attach-badge-${task.id}`}>
                            <Paperclip className="h-3 w-3" />{task.attachments.length}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDetail(task.id)} data-testid={`view-task-${task.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-1">
          {detailLoading ? (
            <Card><CardContent className="py-16 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623] mx-auto" /></CardContent></Card>
          ) : detailTask ? (
            <Card className="border-border/50 sticky top-6" data-testid="task-detail-panel">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-base leading-tight">{detailTask.task_name}</CardTitle>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDetailTask(null)}><X className="h-4 w-4" /></Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge className="bg-amber-500/15 text-amber-600 border-amber-500/20">In Review</Badge></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Priority</span><Badge className={`border ${getPriorityInfo(detailTask.priority, detailTask.department).color}`}>{getPriorityInfo(detailTask.priority, detailTask.department).label} ({detailTask.priority})</Badge></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Due Date</span><span>{new Date(detailTask.due_date).toLocaleDateString()}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Category</span><span>{detailTask.category || '-'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Created By</span><span>{detailTask.created_by_name}</span></div>
                <Separator />
                <div>
                  <p className="text-muted-foreground mb-1">Assigned To</p>
                  {detailTask.work_type === 'Team' && detailTask.assigned_to_names ? (
                    <div className="space-y-0.5">{detailTask.assigned_to_names.map((n, i) => <p key={i} className="font-medium">{n}</p>)}</div>
                  ) : (
                    <p className="font-medium">{detailTask.assigned_to_name}</p>
                  )}
                </div>
                {detailTask.description && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-muted-foreground mb-1">Description</p>
                      <p className="whitespace-pre-wrap">{detailTask.description}</p>
                    </div>
                  </>
                )}
                {detailTask.attachments && detailTask.attachments.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-muted-foreground mb-2 flex items-center gap-1.5">
                        <Paperclip className="h-3.5 w-3.5" /> Attachments ({detailTask.attachments.length})
                      </p>
                      <div className="space-y-1.5" data-testid="approval-attachments">
                        {detailTask.attachments.map(att => {
                          const ext = (att.filename || '').split('.').pop().toLowerCase();
                          const isPreviewable = ext === 'pdf' || ['png','jpg','jpeg','gif','webp'].includes(ext);
                          return (
                            <div
                              key={att.id}
                              className="flex items-center justify-between gap-2 p-2 rounded-md bg-accent/50 hover:bg-accent transition-colors text-left"
                              data-testid={`approval-attachment-${att.id}`}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-xs font-medium truncate">{att.filename}</p>
                                  <p className="text-[10px] text-muted-foreground">{(att.size / 1024).toFixed(1)} KB</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {isPreviewable && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-[10px]"
                                    onClick={() => handleViewAttachment(detailTask.id, att)}
                                    data-testid={`approval-view-${att.id}`}
                                  >
                                    <Eye className="h-3 w-3 mr-1" /> View
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-[10px] text-[#F5A623]"
                                  onClick={() => handleDownloadAttachment(detailTask.id, att)}
                                  data-testid={`approval-download-${att.id}`}
                                >
                                  <Download className="h-3 w-3 mr-1" /> Download
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
                <Separator />
                <Link
                  to={`/tasks/${detailTask.id}`}
                  className="text-xs text-[#F5A623] hover:underline flex items-center gap-1"
                  data-testid="view-full-details-link"
                >
                  <ExternalLink className="h-3 w-3" /> View full task details
                </Link>
                <Separator />
                <div className="space-y-2 pt-1">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" size="sm"
                    onClick={async () => { try { await approveTask(detailTask.id, { action: 'approve' }); toast.success('Approved!'); setDetailTask(null); fetchTasks(); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }}}
                    data-testid="detail-approve-btn">
                    <ShieldCheck className="h-4 w-4 mr-2" /> Approve
                  </Button>
                  <Textarea placeholder="Rejection remark (mandatory)" rows={2} className="text-sm" data-testid="detail-reject-reason"
                    id="detail-reject-input" />
                  <Button variant="destructive" className="w-full" size="sm" data-testid="detail-reject-btn"
                    onClick={async () => {
                      const reason = document.getElementById('detail-reject-input')?.value || '';
                      if (!reason.trim()) { toast.error('Remark is mandatory for rejection'); return; }
                      try { await approveTask(detailTask.id, { action: 'reject', reason }); toast.success('Rejected'); setDetailTask(null); fetchTasks(); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
                    }}>
                    <XCircle className="h-4 w-4 mr-2" /> Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2">
              <CardContent className="py-16 text-center text-muted-foreground text-sm">
                <ChevronRight className="h-8 w-8 mx-auto mb-2 opacity-30" />
                Click a task to see details
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Attachment Preview Dialog — clean fullscreen */}
      <Dialog open={!!previewAtt} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent
          className="!max-w-none w-screen h-screen p-0 m-0 rounded-none border-0 sm:rounded-none bg-black/95 [&>button]:hidden"
          data-testid="attachment-preview-dialog"
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{previewAtt?.name}</DialogTitle>
          </DialogHeader>
          {/* Floating cancel button (wrapped so [&>button]:hidden doesn't hide it) */}
          <div className="absolute top-4 right-4 z-50">
            <button
              type="button"
              onClick={closePreview}
              className="px-4 py-2 rounded-md bg-white/90 hover:bg-white text-black text-sm font-medium flex items-center gap-2 transition-colors shadow-lg"
              data-testid="preview-close-btn"
              aria-label="Cancel preview"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          </div>
          {/* Content */}
          <div className="w-full h-full flex items-center justify-center">
            {previewAtt?.type === 'pdf' ? (
              <iframe
                src={previewAtt.url}
                title={previewAtt.name}
                className="w-full h-full border-0 bg-white"
                data-testid="pdf-preview-iframe"
              />
            ) : previewAtt?.type === 'image' ? (
              <img
                src={previewAtt.url}
                alt={previewAtt.name}
                className="max-w-full max-h-full object-contain"
                data-testid="image-preview-img"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalsPage;
